import { Ref, useCallback, type RefCallback } from 'react';

type PossibleRef<T> = Ref<T> | undefined;

type RefCleanup<T> = ReturnType<RefCallback<T>>;

/**
 * Assigns a value to a ref.
 * If the ref is a function, it calls the function with the value.
 * If the ref is an object with a `current` property, it assigns the value to `current`.
 *
 * @param ref - The ref to assign the value to.
 * @param value - The value to assign to the ref.
 * @returns A cleanup function if the ref is a function, otherwise nothing.
 */
export function assignRef<T>(ref: PossibleRef<T>, value: T): RefCleanup<T> {
  if (typeof ref === 'function') {
    return ref(value);
  } else if (typeof ref === 'object' && ref !== null && 'current' in ref) {
    // @ts-expect-error: current may be readonly, but we intentionally assign here
    ref.current = value;
  }
}

/**
 * Merges multiple refs into a single ref callback.
 * When the merged ref is called with a node, it assigns the node to each of the refs.
 * If any of the refs return a cleanup function, it will be called when the node is set to null.
 *
 * @param refs - An array of refs to merge.
 * @return A ref callback that can be used to assign the node to all refs.
 * @template T - The type of the node that the refs will point to.
 */
export function mergeRefs<T>(...refs: PossibleRef<T>[]): RefCallback<T> {
  const cleanupMap = new Map<PossibleRef<T>, Exclude<RefCleanup<T>, void>>();

  // return cleanup function that will call all cleanup functions
  return (node: T | null) => {
    refs.forEach((ref) => {
      const cleanup = assignRef(ref, node);
      if (typeof cleanup === 'function') {
        cleanupMap.set(ref, cleanup);
      }
    });

    if (cleanupMap.size > 0) {
      return () => {
        refs.forEach((ref) => {
          const cleanup = cleanupMap.get(ref);
          if (typeof cleanup === 'function') {
            // @ts-ignore allow cleanup to be called if defined
            cleanup();
          } else {
            assignRef(ref, null);
          }
        });
        cleanupMap.clear();
      };
    }
  };
}

/**
 * Custom hook to merge multiple refs into a single ref callback.
 *
 * @param refs - An array of refs to merge.
 * @returns A memoized ref callback that can be used to assign the node to all refs.
 * @template T - The type of the node that the refs will point to.
 */
export function useMergedRef<T>(...refs: PossibleRef<T>[]) {
  return useCallback(mergeRefs(...refs), refs);
}